import React from "react";
import useEmblaCarousel from "embla-carousel-react";
import { EmblaOptionsType } from "embla-carousel";
import styled from "styled-components";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { StaticImport } from "next/dist/shared/lib/get-img-props";
import {
    usePrevNextButtons,
    ReviewPrevButton,
    ReviewNextButton
} from "./Component-EmblaCarouselArrowButtons";
import { theme } from "@/config/theme";

interface GalleryImage {
    src: string | StaticImport;
    alt?: string;
}

interface GalleryCarouselProps {
    slides: GalleryImage[];
    options?: EmblaOptionsType;
}

const GalleryCarousel: React.FC<GalleryCarouselProps> = ({
    slides,
    options
}) => {
    const [emblaRef, emblaApi] = useEmblaCarousel(options);

    const {
        prevBtnDisabled,
        nextBtnDisabled,
        onPrevButtonClick,
        onNextButtonClick
    } = usePrevNextButtons(emblaApi);

    const t = useTranslations("LocationGallery");

    return (
        <div>
            <SCWrapper>
                <div className="text-container">
                    <h2 className="text-largexlarge">{t("gallery-title")}</h2>
                    <div className="text-medium">
                        {t("gallery-description")}
                    </div>
                </div>
                <div className="embla__controls">
                    <div className="embla__buttons">
                        <ReviewPrevButton
                            onClick={onPrevButtonClick}
                            disabled={prevBtnDisabled}
                        />
                        <ReviewNextButton
                            onClick={onNextButtonClick}
                            disabled={nextBtnDisabled}
                        />
                    </div>
                </div>
            </SCWrapper>

            <SCEmbla>
                <section className="embla">
                    <div className="embla__viewport" ref={emblaRef}>
                        <div className="embla__container">
                            {slides.map((img, index) => (
                                <div className="embla__slide" key={index}>
                                    <Image
                                        src={img.src}
                                        alt={
                                            img.alt ||
                                            `Gallery image ${index + 1}`
                                        }
                                        fill
                                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                                        style={{ objectFit: "cover" }}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            </SCEmbla>
        </div>
    );
};

export default GalleryCarousel;

const SCWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    --text-body: rgb(54, 49, 61);
    --text-high-contrast-rgb-value: 49, 49, 49;
    --detail-medium-contrast: rgb(234, 234, 234);

    .embla__controls {
        display: grid;
        grid-template-columns: auto 1fr;
        justify-content: space-between;
        // gap: 1.2rem;
        // margin-top: 1.8rem;
    }
    .embla__buttons {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 0.6rem;
        align-items: center;
    }
    .embla__button {
        -webkit-tap-highlight-color: rgba(
            var(--text-high-contrast-rgb-value),
            0.5
        );
        -webkit-appearance: none;
        appearance: none;
        background-color: white;
        touch-action: manipulation;
        display: inline-flex;
        text-decoration: none;
        cursor: pointer;
        border: 0;
        padding: 0;
        margin: 0;
        // box-shadow: inset 0 0 0 0.2rem var(--detail-medium-contrast);
        width: 3.6rem;
        height: 3.6rem;
        z-index: 1;
        // border-radius: 50%;
        border-radius: 10px;
        color: var(--text-body);
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .embla__button:disabled {
        color: var(--detail-high-contrast);
    }
    .embla__button__svg {
        width: 35%;
        height: 35%;
    }
`;

const SCEmbla = styled.div`
    .embla {
        margin: auto;
        --slide-spacing: 1rem;
        --slide-size: 100%;
        --slide-size-sm: 50%;
        --slide-size-lg: 33.333%;

        .embla__viewport {
            overflow: hidden;
        }

        .embla__container {
            display: flex;
        }

        .embla__slide {
            position: relative;
            flex: 0 0 var(--slide-size);
            height: 300px;
            margin-right: var(--slide-spacing);
            border-radius: 10px;
            overflow: hidden;

            @media screen and (min-width: 750px) {
                flex: 0 0 var(--slide-size-sm);
            }

            @media screen and (min-width: 1200px) {
                flex: 0 0 var(--slide-size-lg);
            }
        }
    }
`;
